import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ isSidebarActive, mobileMenu, closeSidebar }) => {
    const location = useLocation();
    const { user } = useAuth();
    const role = user?.role;

    // Helper to determine if a link is active based on query params
    const isActive = (path, queryParam) => {
        if (path === location.pathname) {
            const currentParams = new URLSearchParams(location.search);
            if (!queryParam) {
                // Default view check
                return !currentParams.has('view');
            }
            return currentParams.get('view') === queryParam;
        }
        return false;
    };

    return (
        <aside className={isSidebarActive ? "sidebar active" : mobileMenu ? "sidebar sidebar-open" : "sidebar"}>
            <button onClick={closeSidebar} type="button" className="sidebar-close-btn">
                <Icon icon="radix-icons:cross-2" />
            </button>
            <div>
                <Link to={role === 'SuperAdmin' ? "/super-admin" : "/clinic-admin"} className="sidebar-logo">
                    <Icon icon="solar:medical-kit-bold" className="logo-icon" />
                    <span className="app-brand-text">Medoro</span>
                </Link>
            </div>
            <div className="sidebar-menu-area">
                <ul className="sidebar-menu" id="sidebar-menu">
                    <li className="sidebar-menu-group-title">Menu</li>

                    {/* SUPER ADMIN MENU */}
                    {role === 'SuperAdmin' && (
                        <>
                            <li>
                                <Link to="/super-admin" className={(isActive('/super-admin') && !location.search) || isActive('/super-admin', 'analytics') ? "active-page" : ""}>
                                    <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Management</li>
                            <li>
                                <Link to="/super-admin?view=clinics" className={isActive('/super-admin', 'clinics') ? "active-page" : ""}>
                                    <Icon icon="solar:hospital-bold" className="menu-icon" />
                                    <span>Clinics</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/super-admin?view=plans" className={isActive('/super-admin', 'plans') ? "active-page" : ""}>
                                    <Icon icon="solar:box-minimalistic-bold" className="menu-icon" />
                                    <span>Plans</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/super-admin?view=users" className={isActive('/super-admin', 'users') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-rounded-bold" className="menu-icon" />
                                    <span>Total Users</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Revenue</li>
                            <li>
                                <Link to="/super-admin?view=billing" className={isActive('/super-admin', 'billing') ? "active-page" : ""}>
                                    <Icon icon="solar:bill-list-bold" className="menu-icon" />
                                    <span>Billing & Invoices</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">System</li>

                            <li>
                                <Link to="/super-admin?view=audit-logs" className={isActive('/super-admin', 'audit-logs') ? "active-page" : ""}>
                                    <Icon icon="solar:history-bold" className="menu-icon" />
                                    <span>Audit Logs</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/super-admin?view=support" className={isActive('/super-admin', 'support') ? "active-page" : ""}>
                                    <Icon icon="solar:help-bold" className="menu-icon" />
                                    <span>Support</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/super-admin?view=settings" className={isActive('/super-admin', 'settings') ? "active-page" : ""}>
                                    <Icon icon="solar:settings-bold" className="menu-icon" />
                                    <span>Settings</span>
                                </Link>
                            </li>
                        </>
                    )}

                    {/* CLINIC ADMIN MENU */}
                    {role === 'ClinicAdmin' && (
                        <>

                            <li>
                                <Link to="/clinic-admin" className={(isActive('/clinic-admin') && !location.search) || isActive('/clinic-admin', 'dashboard') ? "active-page" : ""}>
                                    <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Patient Management</li>

                            <li>
                                <Link to="/clinic-admin?view=patients" className={isActive('/clinic-admin', 'patients') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-rounded-bold" className="menu-icon" />
                                    <span>Patients</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/clinic-admin?view=appointments" className={isActive('/clinic-admin', 'appointments') ? "active-page" : ""}>
                                    <Icon icon="solar:calendar-mark-bold" className="menu-icon" />
                                    <span>Appointments</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Staff Management</li>
                            <li>
                                <Link to="/clinic-admin?view=doctors" className={isActive('/clinic-admin', 'doctors') ? "active-page" : ""}>
                                    <Icon icon="solar:stethoscope-bold" className="menu-icon" />
                                    <span>Doctors</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/clinic-admin?view=staff" className={isActive('/clinic-admin', 'staff') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-rounded-bold" className="menu-icon" />
                                    <span>Staff (Assistants)</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Operations</li>
                            <li>
                                <Link to="/clinic-admin?view=mr_visits" className={isActive('/clinic-admin', 'mr_visits') ? "active-page" : ""}>
                                    <Icon icon="solar:user-id-bold" className="menu-icon" />
                                    <span>MR Visits</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Support</li>
                            <li>
                                <Link to="/clinic-admin?view=support" className={isActive('/clinic-admin', 'support') ? "active-page" : ""}>
                                    <Icon icon="solar:help-bold" className="menu-icon" />
                                    <span>Support Tickets</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Finance</li>
                            <li>
                                <Link to="/clinic-admin?view=billing" className={isActive('/clinic-admin', 'billing') ? "active-page" : ""}>
                                    <Icon icon="solar:bill-list-bold" className="menu-icon" />
                                    <span>Billing & Invoices</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Account & Settings</li>
                            <li>
                                <Link to="/clinic-admin?view=clinic_settings" className={isActive('/clinic-admin', 'clinic_settings') ? "active-page" : ""}>
                                    <Icon icon="solar:hospital-bold" className="menu-icon" />
                                    <span>Clinic Profile</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/clinic-admin?view=subscription" className={isActive('/clinic-admin', 'subscription') ? "active-page" : ""}>
                                    <Icon icon="solar:crown-star-bold" className="menu-icon" />
                                    <span>My Subscription</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/clinic-admin?view=settings" className={isActive('/clinic-admin', 'settings') ? "active-page" : ""}>
                                    <Icon icon="solar:settings-bold" className="menu-icon" />
                                    <span>My Profile</span>
                                </Link>
                            </li>
                        </>
                    )}

                    {/* ASSISTANT MENU */}
                    {role === 'Assistant' && (
                        <>
                            <li>
                                <Link to="/assistant-dashboard" className={(isActive('/assistant-dashboard') && !location.search) ? "active-page" : ""}>
                                    <Icon icon="solar:widget-bold" className="menu-icon" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Clinical Operations</li>
                            <li>
                                <Link to="/assistant-dashboard?view=queue" className={isActive('/assistant-dashboard', 'queue') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-two-rounded-bold" className="menu-icon" />
                                    <span>Live Queue</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/assistant-dashboard?view=appointments" className={isActive('/assistant-dashboard', 'appointments') ? "active-page" : ""}>
                                    <Icon icon="solar:calendar-mark-bold" className="menu-icon" />
                                    <span>Appointments</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Team & Operations</li>
                            <li>
                                <Link to="/assistant-dashboard?view=mr_visits" className={isActive('/assistant-dashboard', 'mr_visits') ? "active-page" : ""}>
                                    <Icon icon="solar:user-id-bold" className="menu-icon" />
                                    <span>MR Visits</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Finance</li>
                            <li>
                                <Link to="/assistant-dashboard?view=invoices" className={isActive('/assistant-dashboard', 'invoices') ? "active-page" : ""}>
                                    <Icon icon="solar:bill-list-bold" className="menu-icon" />
                                    <span>Invoices</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Account & Settings</li>
                            <li>
                                <Link to="/assistant-dashboard?view=settings" className={isActive('/assistant-dashboard', 'settings') ? "active-page" : ""}>
                                    <Icon icon="solar:settings-bold" className="menu-icon" />
                                    <span>Settings</span>
                                </Link>
                            </li>
                        </>
                    )}

                    {/* DOCTOR MENU */}
                    {role === 'Doctor' && (
                        <>
                            <li>
                                <Link to="/doctor-dashboard" className={(isActive('/doctor-dashboard') && !location.search) || isActive('/doctor-dashboard', 'dashboard') ? "active-page" : ""}>
                                    <Icon icon="solar:widget-bold" className="menu-icon" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Clinical Practice</li>
                            <li>
                                <Link to="/doctor-dashboard?view=schedule" className={isActive('/doctor-dashboard', 'schedule') ? "active-page" : ""}>
                                    <Icon icon="solar:calendar-date-bold" className="menu-icon" />
                                    <span>My Schedule</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/doctor-dashboard?view=patients" className={isActive('/doctor-dashboard', 'patients') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-rounded-bold" className="menu-icon" />
                                    <span>My Patients</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/doctor-dashboard?view=appointments" className={isActive('/doctor-dashboard', 'appointments') ? "active-page" : ""}>
                                    <Icon icon="solar:clipboard-list-bold" className="menu-icon" />
                                    <span>Appointments</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Team & Operations</li>
                            <li>
                                <Link to="/doctor-dashboard?view=team" className={isActive('/doctor-dashboard', 'team') ? "active-page" : ""}>
                                    <Icon icon="solar:users-group-rounded-bold" className="menu-icon" />
                                    <span>Assistants</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/doctor-dashboard?view=mr_visits" className={isActive('/doctor-dashboard', 'mr_visits') ? "active-page" : ""}>
                                    <Icon icon="solar:user-id-bold" className="menu-icon" />
                                    <span>MR Visits</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Finance</li>
                            <li>
                                <Link to="/doctor-dashboard?view=invoices" className={isActive('/doctor-dashboard', 'invoices') ? "active-page" : ""}>
                                    <Icon icon="solar:bill-list-bold" className="menu-icon" />
                                    <span>Invoices</span>
                                </Link>
                            </li>
                            <li className="sidebar-menu-group-title">Profile</li>
                            <li>
                                <Link to="/doctor-dashboard?view=settings" className={isActive('/doctor-dashboard', 'settings') ? "active-page" : ""}>
                                    <Icon icon="solar:settings-bold" className="menu-icon" />
                                    <span>Profile</span>
                                </Link>
                            </li>
                        </>
                    )}


                    {/* PATIENT MENU */}
                    {role === 'Patient' && (
                        <>
                            <li>
                                <Link to="/patient-dashboard?view=overview" className={(isActive('/patient-dashboard') && !location.search) || isActive('/patient-dashboard', 'overview') ? "active-page" : ""}>
                                    <Icon icon="solar:home-smile-angle-outline" className="menu-icon" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Health Tools</li>
                            <li>
                                <Link to="/patient-dashboard?view=symptom_checker" className={isActive('/patient-dashboard', 'symptom_checker') ? "active-page" : ""}>
                                    <Icon icon="solar:stethoscope-bold" className="menu-icon" />
                                    <span>Symptom Checker</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Consultation</li>
                            <li>
                                <Link to="/patient-dashboard?view=find_doctor" className={isActive('/patient-dashboard', 'find_doctor') ? "active-page" : ""}>
                                    <Icon icon="solar:stethoscope-bold" className="menu-icon" />
                                    <span>Find Doctor</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Appointments</li>
                            <li>
                                <Link to="/patient-dashboard?view=appointments" className={isActive('/patient-dashboard', 'appointments') ? "active-page" : ""}>
                                    <Icon icon="solar:calendar-mark-bold" className="menu-icon" />
                                    <span>My Appointments</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/patient-dashboard?view=reschedule_needed" className={isActive('/patient-dashboard', 'reschedule_needed') ? "active-page" : ""}>
                                    <Icon icon="solar:calendar-add-bold" className="menu-icon" />
                                    <span>Reschedule Needed</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Records & Billing</li>
                            <li>
                                <Link to="/patient-dashboard?view=records" className={isActive('/patient-dashboard', 'records') ? "active-page" : ""}>
                                    <Icon icon="solar:folder-with-files-bold" className="menu-icon" />
                                    <span>Medical Records</span>
                                </Link>
                            </li>
                            <li>
                                <Link to="/patient-dashboard?view=invoices" className={isActive('/patient-dashboard', 'invoices') ? "active-page" : ""}>
                                    <Icon icon="solar:bill-list-bold" className="menu-icon" />
                                    <span>My Invoices</span>
                                </Link>
                            </li>

                            <li className="sidebar-menu-group-title">Account</li>
                            <li>
                                <Link to="/patient-dashboard?view=profile" className={isActive('/patient-dashboard', 'profile') ? "active-page" : ""}>
                                    <Icon icon="solar:user-circle-bold" className="menu-icon" />
                                    <span>My Profile</span>
                                </Link>
                            </li>
                        </>
                    )}
                </ul>
            </div>
        </aside >
    );
};

export default Sidebar;
